import React, { useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
} from "@mui/material";
import { X } from "lucide-react";
import { useTheme } from "../../../../core/providers/ThemeProvider";
import { DialogWrapper } from "./DialogWrapper";
import acceptIcon from "../../../../assets/accept.svg";
import { useGetOrderDetailsMutation } from "../../../../api/travellerApi3";

interface EditCartRequestDialogProps {
  open: boolean;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
  editDetails?: {
    item_id?: string;
    order_code?: string;
    senderName?: string;
    oldValues?: {
      category?: string;
      weight?: number;
      price?: number;
      pieces?: number;
      pages?: number;
    };
    newValues?: {
      category?: string;
      weight?: number;
      price?: number;
      pieces?: number;
      pages?: number;
    };
  };
  isLoading?: boolean;
}

const EditCartRequestDialog: React.FC<EditCartRequestDialogProps> = ({
  open,
  onClose,
  onApprove,
  onReject,
  editDetails,
  isLoading = false,
}) => {
  const { theme } = useTheme();
  const primaryColor = theme?.colors?.primary || "#2B7A4B";

  // Fetch order details
  const getOrderDetailsMutation = useGetOrderDetailsMutation();

  useEffect(() => {
    if (open && editDetails?.order_code) {
      getOrderDetailsMutation.mutate({ order_code: editDetails.order_code });
    }
  }, [open, editDetails?.order_code]);

  const orderData = getOrderDetailsMutation.data;
  const item = orderData?.items?.find((i: unknown) => i.id === editDetails?.item_id);

  // Check if we have new values to display (from notification data)
  const hasNewValues = editDetails?.newValues && (
    editDetails.newValues.weight ||
    editDetails.newValues.price ||
    editDetails.newValues.pieces ||
    editDetails.newValues.pages
  );

  const renderEditField = (fieldName: string, editValue: unknown) => {
    // Don't render if value is null, undefined, empty string, or appears to be zero
    if (editValue === null || editValue === undefined || editValue === "") {
      return null;
    }

    // Check if the value appears to be zero (handles both string and number formats)
    const stringValue = String(editValue).trim();
    if (stringValue === "0" || stringValue === "0.00" || stringValue === "0.0") {
      return null;
    }

    // For numeric-looking values, check if they parse to <= 0
    const numericValue = parseFloat(stringValue);
    if (!isNaN(numericValue) && numericValue <= 0) {
      return null;
    }

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1.5,
          borderBottom: "1px solid #E9EAEB",
          "&:last-child": {
            borderBottom: "none",
          },
        }}
      >
        <Typography
          sx={{
            fontFamily: "Poppins",
            fontSize: "13px",
            fontWeight: 500,
            color: "#484A54",
          }}
        >
          {fieldName}
        </Typography>
        <Typography
          sx={{
            fontFamily: "Poppins",
            fontSize: "13px",
            fontWeight: 600,
            color: primaryColor,
          }}
        >
          {editValue}
        </Typography>
      </Box>
    );
  };

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-labelledby="edit-cart-request-title"
      aria-describedby="edit-cart-request-description"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow:
            "0px 20px 24px -4px rgba(10, 13, 18, 0.08), 0px 8px 8px -4px rgba(10, 13, 18, 0.03)",
          width: { xs: "95%", sm: "540px" },
          maxWidth: "540px",
        },
      }}
    >

      {/* Accept Icon */}
      <Box
        sx={{
          position: "absolute",
          top: 24,
          left: 24,
          width: "48px",
          height: "48px",
          pointerEvents: "none",
          zIndex: 1,
        }}
      >
        <img src={acceptIcon} alt="Accept" style={{ width: "100%", height: "100%" }} />
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "32px 24px 24px",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={onClose}
          aria-label="Close dialog"
          disabled={isLoading}
          sx={{
            position: "absolute",
            right: "16px",
            top: "16px",
            width: "44px",
            height: "44px",
            borderRadius: "8px",
            zIndex: 2,
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <X size={24} color="#A4A7AE" />
        </IconButton>

        {/* Title */}
        <Typography
          id="edit-cart-request-title"
          sx={{
            fontFamily: "Poppins",
            fontWeight: 600,
            fontSize: "22px",
            lineHeight: "32px",
            color: "#181D27",
            mt: 8,
            mb: 1,
          }}
        >
          Edit Request Received
        </Typography>

        {/* Description */}
        <Typography
          id="edit-cart-request-description"
          sx={{
            fontFamily: "Poppins",
            fontWeight: 400,
            fontSize: "14px",
            lineHeight: "21px",
            color: "#667071",
            mb: 3,
          }}
        >
          {editDetails?.senderName ? (
            <>
              <strong>{editDetails.senderName}</strong> has requested to edit an item in the order.
            </>
          ) : (
            "A sender has requested to edit an item in the order."
          )}
        </Typography>

      

        {/* Actions - Approve and Reject buttons side by side */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "12px",
            width: "100%",
          }}
        >
          {/* Approve Button */}
          <Button
            onClick={onApprove}
            disabled={isLoading}
            sx={{
              flex: 1,
              padding: "12px 20px",
              height: "44px",
              background: primaryColor,
              borderRadius: "8px",
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "14px",
              color: "#FFFFFF",
              textTransform: "none",
              "&:hover": {
                background: `${primaryColor}DD`,
              },
              "&:disabled": {
                background: "#E9EAEB",
                color: "#A4A7AE",
              },
            }}
          >
            {isLoading ? "Processing..." : "Approve"}
          </Button>

          {/* Reject Button */}
          <Button
            onClick={onReject}
            disabled={isLoading}
            sx={{
              flex: 1,
              padding: "12px 20px",
              height: "44px",
              background: "#FFFFFF",
              border: "1px solid #E53E3E",
              borderRadius: "8px",
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "14px",
              color: "#E53E3E",
              textTransform: "none",
              "&:hover": {
                background: "#FFF5F5",
              },
              "&:disabled": {
                borderColor: "#E9EAEB",
                color: "#A4A7AE",
              },
            }}
          >
            Reject
          </Button>
        </Box>
      </Box>
    </DialogWrapper>
  );
};

export default EditCartRequestDialog;
